/* eslint react-hooks/exhaustive-deps: "warn" */

import { type CSSProperties, type PointerEvent, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { number } from 'libs/number';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import styles from './BusinessCreditRiskDecisionCard.module.scss';

const SCORE = 42;
const SCORE_MAX = 100;
const LIMIT = 120000;

const AGING_BUCKETS = [
  { label: 'Current', pct: 38, tone: 'green' },
  { label: '1–30', pct: 29, tone: 'lime' },
  { label: '31–60', pct: 18, tone: 'amber' },
  { label: '61+', pct: 15, tone: 'red' },
] as const;

const useTween = (target: number, active: boolean, duration = 1100) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    let raf = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));

      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);

  return value;
};

export const BusinessCreditRiskDecisionCard = () => {
  const { t } = useInt();
  const frameRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const inView = useIntersection(frameRef, { threshold: 0.35 });

  const score = useTween(SCORE, inView);
  const limit = useTween(LIMIT, inView, 1200);

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || !cardRef.current || !frameRef.current) {
      return;
    }

    const bounds = frameRef.current.getBoundingClientRect();
    const px = (event.clientX - bounds.left) / bounds.width;
    const py = (event.clientY - bounds.top) / bounds.height;
    const rx = (0.5 - py) * 8;
    const ry = (px - 0.5) * 8;

    cardRef.current.style.transform = `perspective(1200px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
  };

  const handlePointerLeave = () => {
    if (cardRef.current) {
      cardRef.current.style.transform = '';
    }
  };

  return (
    <div className={styles.frame} ref={frameRef} onPointerMove={handlePointerMove} onPointerLeave={handlePointerLeave}>
      <div className={styles.glowRing} />

      <div className={styles.card} ref={cardRef}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.name}>Ardenfield Materials Ltd</h2>
            <p className={styles.reg}>
              England &amp; Wales &middot; <span className={styles.mono}>12045781</span> &middot; {t('incorporated')}{' '}
              <span className={styles.mono}>14 Mar 2019</span>
            </p>
          </div>
          <span className={styles.sample}>{t('Sample record')}</span>
        </div>

        <div className={styles.live}>
          <i className={styles.liveDot} />
          {t('Recalculated the moment a new filing lands')}
        </div>

        <div className={styles.sep} />

        <div className={styles.row}>
          <span className={styles.label}>{t('Credit score')}</span>
          <span className={styles.score}>
            {score}
            <i>/{SCORE_MAX}</i>
          </span>
        </div>
        <div className={styles.bar}>
          <span className={cn(styles.marker, inView && styles.markerOn)} style={{ left: inView ? `${SCORE}%` : 0 }}>
            <span className={styles.markerTip}>{score}</span>
          </span>
        </div>
        <div className={styles.barLabels}>
          <span>0 &middot; {t('high risk')}</span>
          <span>100 &middot; {t('low risk')}</span>
        </div>

        <div className={styles.sep} />

        <div className={styles.row}>
          <div>
            <span className={styles.label}>{t('Recommended limit')}</span>
            <div className={styles.limit}>&pound;{number(limit)}</div>
          </div>
          <span className={styles.revised}>{t('Revised')} 08 Nov 2025</span>
        </div>

        <div className={styles.sep} />

        <div className={cn(styles.block, inView && styles.blockOn)} style={{ '--delay': '0.15s' } as CSSProperties}>
          <div className={styles.blockHead}>
            <span className={styles.label}>{t('Trade payment behaviour')}</span>
            <span className={cn(styles.badge, styles.badgeNeg)}>+14 DBT</span>
          </div>
          <div className={styles.aging}>
            {AGING_BUCKETS.map((bucket, i) => (
              <div className={styles.agingRow} key={bucket.label}>
                <span className={styles.agingKey}>{bucket.label === 'Current' ? t('Current') : bucket.label}</span>
                <div className={styles.agingTrack}>
                  <span
                    className={cn(styles.agingFill, styles[`tone${bucket.tone}`])}
                    style={{ width: inView ? `${bucket.pct}%` : 0, transitionDelay: `${0.25 + i * 0.09}s` }}
                  />
                </div>
                <span className={styles.agingVal}>{bucket.pct}%</span>
              </div>
            ))}
          </div>
          <p className={styles.foot}>
            9 {t('contributing suppliers')} &middot; 34 {t('trade lines')} &middot; {t('industry median 27 days')}
          </p>
        </div>

        <div className={styles.sep} />

        <div className={cn(styles.block, inView && styles.blockOn)} style={{ '--delay': '0.32s' } as CSSProperties}>
          <div className={styles.blockHead}>
            <span className={styles.label}>{t('Court judgments')}</span>
            <span className={cn(styles.badge, styles.badgeNeg)}>{t('1 unsatisfied')}</span>
          </div>
          <div className={styles.judgment}>
            <div>
              <b>County Court Business Centre</b>
              <span className={styles.judgmentSub}>
                {t('Case')} <span className={styles.mono}>D9F2K841</span> &middot; {t('registered')}{' '}
                <span className={styles.mono}>08 Nov 2025</span>
              </span>
            </div>
            <div className={styles.judgmentValue}>
              &pound;11,400
              <span className={styles.judgmentStat}>{t('Unsatisfied')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
